import React from "react";
import { Modal, Stack, Button, Text, Badge, Group, Box } from "@mantine/core";
import { IconClock } from "@tabler/icons-react";
import type { OCRExtraction } from "../../types";

interface OCRExtractionModalProps {
  /**
   * Whether the modal is open
   */
  opened: boolean;
  /**
   * Callback to close the modal
   */
  onClose: () => void;
  /**
   * List of OCR extractions to choose from
   */
  extractions: OCRExtraction[];
  /**
   * Callback when an extraction is selected
   */
  onSelect: (extractionId: number) => void;
  /**
   * Document name for display
   */
  documentName: string;
}

/**
 * Modal that prompts user to select which OCR extraction to view/edit
 * when entering edit mode on a document with multiple extractions.
 */
export const OCRExtractionModal: React.FC<OCRExtractionModalProps> = ({
  opened,
  onClose,
  extractions,
  onSelect,
  documentName,
}) => {
  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format user type for display
  const formatUserType = (userType: string): string => {
    const userTypeMap: Record<string, string> = {
      taxClient: "Tax Client",
      spouse: "Spouse",
      dependent: "Dependent",
    };
    return userTypeMap[userType] || userType;
  };

  // Sort extractions by creation date (newest first)
  const sortedExtractions = [...extractions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleSelect = (extractionId: number) => {
    onSelect(extractionId);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Select OCR Extraction"
      size="md"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This document has multiple OCR extractions. Please select which one
          you'd like to view:
        </Text>
        <Text size="sm" fw={500}>
          Document: {documentName}
        </Text>

        <Stack gap="xs">
          {sortedExtractions.map((extraction, index) => {
            const isLatest = index === 0;

            return (
              <Box
                key={extraction.id}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                  e.currentTarget.style.borderColor = "#228be6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#e0e0e0";
                }}
                onClick={() => handleSelect(extraction.id)}
              >
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      {formatUserType(extraction.userType)} - {extraction.form}
                    </Text>
                    {isLatest && (
                      <Badge size="sm" color="blue" variant="light">
                        Latest
                      </Badge>
                    )}
                  </Group>
                  <Group gap="xs">
                    <IconClock size={12} />
                    <Text size="xs" c="dimmed">
                      {formatDate(extraction.createdAt)}
                    </Text>
                  </Group>
                  {extraction.ocrReview?.ocr_quality != null && (
                    <Text size="xs" c="dimmed">
                      Quality: {String(extraction.ocrReview.ocr_quality)}
                    </Text>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
